import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentNavigation, type ContentNavigationProps } from "@appcaire/ui";

/** Shared public-site adapter: crawlable anchors, client-side page links and reading progress. */
export function ContentSidebar({
  trackSections = false,
  ...props
}: ContentNavigationProps & { trackSections?: boolean }) {
  const navigate = useNavigate();
  const [active, setActive] = useState("");
  const ids = props.items
    .flatMap((item) => (item.href.startsWith("#") ? [item.href.slice(1)] : []))
    .join("|");
  useEffect(() => {
    if (!trackSections) return;
    const sections = ids
      .split("|")
      .map((id) => document.getElementById(id))
      .filter((element): element is HTMLElement => Boolean(element));
    let frame = 0;
    const update = () => {
      frame = 0;
      setActive(
        sections
          .filter((section) => section.getBoundingClientRect().top <= 180)
          .at(-1)?.id ??
          sections[0]?.id ??
          "",
      );
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("hashchange", schedule);
    window.addEventListener("resize", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("hashchange", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [trackSections, ids]);
  return (
    <ContentNavigation
      {...props}
      items={props.items.map((item) =>
        trackSections && item.href.startsWith("#")
          ? {
              ...item,
              current: item.href === `#${active}` ? "location" : undefined,
            }
          : item,
      )}
      onNavigate={(event, href) => {
        if (
          href.startsWith("/") &&
          event.button === 0 &&
          !event.metaKey &&
          !event.ctrlKey &&
          !event.shiftKey &&
          !event.altKey
        ) {
          event.preventDefault();
          navigate(href);
        }
      }}
    />
  );
}
